import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Button } from '@components/ui';
import { usePermission } from '@hooks/usePermission';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { NotificationDropdown } from './NotificationDropdown';

/**
 * Bell icon + unread badge for the header. Click toggles a popover with the
 * last 10 notifications (`NotificationDropdown`). Gated by the
 * `showNotifications` permission so tenants without the feature flag don't
 * see it. Click-outside / Escape closes the popover.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const canSee = usePermission('showNotifications');

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data } = useUnreadCount(canSee);
  const count = data?.count ?? 0;
  const display = count > 99 ? '99+' : String(count);

  // Click outside + Escape to close
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!canSee) return null;

  const label = t('notifications.bell_label', {
    defaultValue: isRtl ? 'الإشعارات' : 'Notifications',
  });

  return (
    <div className="relative" ref={wrapperRef}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span
            aria-hidden
            className="absolute -top-0.5 -end-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-danger text-danger-foreground text-[10px] font-bold leading-none flex items-center justify-center shadow-sm ring-2 ring-surface"
          >
            {display}
          </span>
        )}
        <span className="sr-only">
          {count > 0
            ? t('notifications.bell_unread_count', {
                count,
                defaultValue: isRtl ? `${count} إشعار غير مقروء` : `${count} unread`,
              })
            : label}
        </span>
      </Button>

      {open && (
        <div className="absolute end-0 mt-2 z-30">
          <NotificationDropdown onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
